import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import GraphQLWrapper from '../UI/GraphQLWrapper/GraphQLWrapper';

import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { isEmpty } from '../../Utils/helpers';
import { REGIONS_VALUES_DICTIONARY } from '../../Constants/constants';
import SelectedData from './SelectedData';
import CustomModal from '../UI/Modal/Modal';
import { useLimitUrlsForEnv } from './useLimitUrls';
import TreeComponent from './Tree';
import Filters from './Filters/Filters';
import {
  setCurrentTree,
  setOriginalTree,
  setSelectedKeys,
  setSelectedUrls,
  setFilters,
} from '../../App/Slices/RunTests';
import { getTRFXTreeDS2 } from '../../Utils/treeProcessing';
import TestConfigPanel from './TestConfigPanel';
import { Badge } from 'primereact/badge';
import { useDebounce } from './hooks/useDebounce';
import { useSendUrls } from './hooks/useSendUrlsLangFlow';

function RunTests({
  implementedComponents,
  loading,
  env,
  setEnv,
  runTests,
  getRunTestsVariables,
  redirectUrl,
  prevName,
  prevDescription,
  wasSmokeTest,
  filtersDefaultValues,
  executedUrls,
  region,
  isEditMode,
  editExecution,
  getEditExecutionVariables,
  duplicateExecution,
  includedTrackingValidations,
  executionType,
}) {
  const { testExecutionId = false } = useParams();

  const data = useSelector((state) => state.runTests.data);
  const originalTree = useSelector((state) => state.runTests.originalTree);
  const currentTree = useSelector((state) => state.runTests.currentTree);
  const filters = useSelector((state) => state.runTests.filters);
  //Selected urls structure: [{url: '', components: [''], contextId: ''}, ...]
  const selectedUrls = useSelector((state) => state.runTests.selectedUrls);
  const selectedKeys = useSelector((state) => state.runTests.selectedKeys);

  const [testConfig, setTestConfig] = useState({
    name: '',
    description: '',
    env: '',
    region: 'us-east-1',
    smokeTest: false,
    includeTracking: false,
    executionType: 'standard',
    scan: false,
  });
  const [availableUrls, setAvailableUrls] = useState(0);
  const [showEditedConfirmationModal, setShowEditedConfirmationModal] =
    useState(false);
  const [showComponentConfirmationModal, setShowComponentConfirmationModal] =
    useState(false);

  const [urlFilterValue, setUrlFilterValue] = useState('');
  const debouncedUrlFilter = useDebounce(urlFilterValue, 300);

  const { isLimitUrls } = useLimitUrlsForEnv(redirectUrl, env, selectedUrls);
  const {
    sendUrls,
    loading: loadingScan,
    result: scanResult,
    error: scanError,
  } = useSendUrls();

  var isDuplicate = window.location.href.includes('duplicate');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onNodeChecked = (selectedLeafs) => {
    dispatch(setSelectedUrls(selectedLeafs));
  };

  useEffect(() => {
    if (!isEditMode && !isDuplicate) return;

    setTestConfig({
      name: prevName,
      description: prevDescription,
      env: env,
      region: REGIONS_VALUES_DICTIONARY[region],
      smokeTest: wasSmokeTest,
      includeTracking: includedTrackingValidations,
      executionType: executionType,
    });
  }, [
    prevName,
    wasSmokeTest,
    region,
    prevDescription,
    includedTrackingValidations,
    executionType,
  ]);

  useEffect(() => {
    if (data) {
      const treeData = getTRFXTreeDS2(data);
      dispatch(setOriginalTree(treeData));
      dispatch(setCurrentTree(treeData));
    }
  }, [data]);

  const getLeafs = (node, solveSelected) => {
    if (!node.children) {
      if (selectedKeys?.[node.key]?.checked) {
        solveSelected.push({
          url: node.label,
          components: node.info.components,
          contextId: node.info.contextId,
        });
      }
      return 1;
    }
    let solveLeaf = 0;
    node.children.forEach((child) => {
      solveLeaf += getLeafs(child, solveSelected);
    });
    return solveLeaf;
  };

  useEffect(() => {
    if (currentTree) {
      let leafCount = 0;
      const selectedLeafs = currentTree.reduce((acc, node) => {
        leafCount += getLeafs(node, acc);
        return acc;
      }, []);
      dispatch(setSelectedUrls(selectedLeafs));
      setAvailableUrls(leafCount);
    }
  }, [currentTree]);

  useEffect(() => {
    // We take all the current filters and replace only the 'url' one
    const nweFilters = filters.map((f) =>
      f.name === 'url' ? { ...f, value: debouncedUrlFilter } : f
    );

    handleFilterChange(nweFilters);
  }, [debouncedUrlFilter]);

  const filterNode = (node, depth = 0, currentFilters) => {
    // Filter by data depending on tree level (application, client, etc.)
    if (
      Array.isArray(currentFilters[depth].value) && // solo si es array
      currentFilters[depth].value.length > 0 &&
      !currentFilters[depth].value.includes(node.data)
    ) {
      return null;
    }

    // Filter by URL (applies only to leaf nodes, uses node.label as URL)
    const urlFilter = currentFilters.find((f) => f.name === 'url')?.value || '';
    if (typeof urlFilter === 'string' && urlFilter.trim().length > 0) {
      if (!node.children) {
        if (!node.label.toLowerCase().includes(urlFilter.toLowerCase())) {
          return null;
        }
      }
    }

    // If node has children, filter them recursively
    if (node.children) {
      const filteredChildren = node.children
        .map((child) => filterNode(child, depth + 1, currentFilters))
        .filter(Boolean);

      if (filteredChildren.length === 0) {
        return null;
      }

      return {
        ...node,
        children: filteredChildren,
      };
    }

    // Filter by components
    const componentFilter =
      currentFilters.find((f) => f.name === 'component')?.value || [];
    if (
      Array.isArray(componentFilter) &&
      componentFilter.length > 0 &&
      !componentFilter.every((component) =>
        node.info.components.includes(component)
      )
    ) {
      return null;
    }

    // Leaf node that passed all filters
    return node;
  };

  const handleFilterChange = (currentFilters) => {
    // Get the current tree data
    const treeData = originalTree;

    // Apply filters to tree data
    const filteredData = treeData
      .map((node) => filterNode(node, 0, currentFilters))
      .filter(Boolean);

    // Update the tree view with filtered data
    dispatch(setCurrentTree(filteredData));
  };

  function validateTestConfig() {
    if (testConfig.name === '') {
      window.flash(`Please enter test execution name`, 'info');
      return false;
    }
    if (testConfig.description === '') {
      window.flash(`Please enter test execution description`, 'info');
      return false;
    }
    return true;
  }

  // Reset all filters, selections, and inputs
  const resetAllSelections = () => {
    // Reset filters to empty values
    const resetFilters = filters.map((filter) => ({ ...filter, value: [] }));
    dispatch(setFilters(resetFilters));

    // Clear tree selections
    dispatch(setSelectedKeys({}));
    dispatch(setSelectedUrls([]));

    // Reset test config
    setTestConfig({
      name: '',
      description: '',
      env: '',
      region: 'us-east-1',
      smokeTest: false,
      includeTracking: false,
      executionType: 'standard',
    });
  };

  const onSubmit = async (event, allowEmptyComponents = false) => {
    if (!validateTestConfig()) return;

    if (isEmpty(selectedUrls)) {
      window.flash(`There are no urls selected to run tests`, 'info');
      return;
    }
    setShowComponentConfirmationModal(false);

    if (
      !allowEmptyComponents &&
      filters.find((f) => f.name === 'component')?.value?.length === 0
    ) {
      setShowComponentConfirmationModal(true);
      return;
    }

    runTests({
      variables: getRunTestsVariables(testConfig, selectedUrls, filters),
    }).then(() => {
      // Reset all selections after successful test run
      resetAllSelections();
      navigate(redirectUrl);
    });
  };

  // Scan functionality with LangFlow-agent
  const onScan = async () => {
    if (testConfig.scan) {
      const urlsArray = selectedUrls.map((node) => node.url);

      console.log('Enviando URLs a LangFlow:', urlsArray);

      await sendUrls(urlsArray);

      if (scanError) {
        window.flash(`Error executing scan: ${scanError}`, 'error');
        return;
      }

      window.flash(`Scan completed! Result: ${scanResult}`, 'success');
      resetAllSelections();
      navigate(redirectUrl);
      return;
    }
  };

  const saveEditedHandler = () => {
    setShowEditedConfirmationModal(false);

    if (!validateTestConfig()) return;

    editExecution({
      variables: getEditExecutionVariables(
        testExecutionId,
        testConfig,
        selectedUrls,
        filters
      ),
    }).then(() => {
      // Reset all selections after successful edit
      resetAllSelections();
      navigate(redirectUrl);
    });
  };

  const saveDuplicateHandler = () => {
    if (!validateTestConfig()) return;

    duplicateExecution({
      variables: getRunTestsVariables(testConfig, selectedUrls, filters),
    }).then(() => {
      // Reset all selections after successful duplication
      resetAllSelections();
      navigate(redirectUrl);
    });
  };

  const onSaveEdited = () => {
    if (!validateTestConfig()) return;

    if (isEmpty(selectedUrls)) {
      window.flash(`There are no urls selected to run tests`, 'info');
      return;
    }
    setShowEditedConfirmationModal(true);
    return;
  };

  const onChangeTestConfig = (newTestConfigName, newTestConfigValue) => {
    setTestConfig({ ...testConfig, [newTestConfigName]: newTestConfigValue });
  };

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      resetAllSelections();
    };
  }, []);

  useEffect(() => {
    // When we change the environment, we reset filters, selected URLs and testConfig
    resetAllSelections();
    // We also clean the URL input
    setUrlFilterValue('');

    setTestConfig((prev) => ({ ...prev, env }));
  }, [env]);

  const view = (
    <div className='control-section justify-content-center'>
      <div id='wrapper' className='run-tests-wrapper'>
        <div id='head'>
          <TestConfigPanel
            env={env}
            setEnv={setEnv}
            testConfig={testConfig}
            onChangeTestConfig={onChangeTestConfig}
            isEditMode={isEditMode}
            isDuplicate={isDuplicate}
            isLimitUrls={isLimitUrls}
            onSaveEdited={onSaveEdited}
            saveDuplicateHandler={saveDuplicateHandler}
            onSubmit={onSubmit}
            onScan={onScan}
          />
        </div>
        <div className='content'>
          <div className='e-card'>
            <Filters
              data={originalTree}
              implementedComponents={implementedComponents}
              onChange={handleFilterChange}
              urlFilterValue={urlFilterValue}
              setUrlFilterValue={setUrlFilterValue}
            />
            <div className='e-card-content'>
              <div className='row'>
                <div className='col-md-5 col-sm-12 contexts-tree-container'>
                  <div className='control-section urls-section'>
                    <Badge
                      value={`Available URLs (${availableUrls})`}
                      severity='secondary'
                    />
                  </div>
                  <div style={{ height: '400px', overflow: 'auto' }}>
                    <TreeComponent
                      data={currentTree}
                      onSelectionChange={onNodeChecked}
                      selectedKeys={selectedKeys}
                      setSelectedKeys={(keys) =>
                        dispatch(setSelectedKeys(keys))
                      }
                    />
                  </div>
                </div>
                <div className='col-md-7 col-sm-12 selected-urls-container'>
                  <SelectedData data={selectedUrls.map((node) => node.url)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomModal
        show={showEditedConfirmationModal}
        title='Once the Tests Execution is saved the results and data will be deleted.
      Are you sure you want to continue the edition process?'
        onClose={() => setShowEditedConfirmationModal(false)}
        onAccept={() => saveEditedHandler()}
      />
      <CustomModal
        show={showComponentConfirmationModal}
        title='There are no components selected for this execution. Are you sure you want to run the tests over all the components?'
        onClose={() => setShowComponentConfirmationModal(false)}
        onAccept={(event) => onSubmit(event, true)}
      />
    </div>
  );
  return <GraphQLWrapper component={view} loading={loading} />;
}

export default RunTests;
